import { APIProvider, AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { DeliveryRow, DriverStatusRow } from '@torapon/shared';
import { api } from '../lib/api';
import { AlertsPanel } from '../components/AlertsPanel';
import { useNames } from '../lib/directory';
import { useDriverStatus } from '../lib/status';
import { useLive, useTicker } from '../lib/realtime';
import { STATE, ago } from '../lib/driverState';

const KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;
const SAPPORO = { lat: 43.0621, lng: 141.3544 };

/** トラックの印：地図の色。通信なしは灰色（仕様書「配車マップ」） */
function Truck({ r, name, focused }: { r: DriverStatusRow; name: string; focused: boolean }) {
  const gray = r.state === 'no_signal';
  return (
    <div title={`${name}・${STATE[r.state][0]}`} style={{ display: 'grid', justifyItems: 'center', gap: 2, transform: focused ? 'scale(1.2)' : undefined }}>
      <span style={{ background: gray ? '#9e9e9e' : r.color, color: '#fff', borderRadius: 10, padding: '4px 8px', fontWeight: 800, fontSize: 12, boxShadow: '0 2px 6px rgba(0,0,0,.3)', outline: r.state === 'unknown_stop' ? '3px solid var(--alert)' : focused ? '3px solid #000' : 'none' }}>{name}</span>
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: gray ? '#9e9e9e' : r.color, border: '2px solid #fff' }} />
    </div>
  );
}

/** ?driver= のトラックへ地図を動かす */
function FocusOn({ pos }: { pos: { lat: number; lng: number } | null }) {
  const map = useMap();
  useEffect(() => { if (map && pos) { map.panTo(pos); map.setZoom(15); } }, [map, pos?.lat, pos?.lng]);
  return null;
}

/** 配車マップ。位置が届くとすぐにトラックを動かす（WebSocket） */
export function MapPage() {
  const names = useNames();
  const [sp] = useSearchParams();
  const focus = sp.get('driver');
  const { rows } = useDriverStatus();
  const [deliveries, setDeliveries] = useState<DeliveryRow[]>([]);
  const loadDeliveries = () => api.get<{ deliveries: DeliveryRow[] }>('/v1/office/deliveries').then((r) => setDeliveries(r.deliveries)).catch(() => {});
  useEffect(() => { void loadDeliveries(); }, []);
  useLive(['deliveries'], loadDeliveries);
  useTicker();
  const status = rows ?? [];
  const working = status.filter((r) => r.shiftId);
  const trucks = status.filter((r) => r.position);
  const unassigned = deliveries.filter((d) => d.status === 'unassigned').length;
  const focused = status.find((r) => r.driverId === focus)?.position ?? null;
  return (
    <div className="page full" style={{ position: 'relative', height: 'calc(100vh / var(--z) - 64px)' }}>
      {KEY ? (
        <APIProvider apiKey={KEY}>
          <Map defaultCenter={SAPPORO} defaultZoom={12} mapId="torapon" gestureHandling="greedy" disableDefaultUI style={{ width: '100%', height: '100%' }}>
            <AdvancedMarker position={SAPPORO} title="札幌本社" />
            {trucks.map((r) => (
              <AdvancedMarker key={r.driverId} position={r.position!} title={names.driver(r.driverId)}>
                <Link to={`/drivers/${r.driverId}`}><Truck r={r} name={names.driver(r.driverId)} focused={r.driverId === focus} /></Link>
              </AdvancedMarker>
            ))}
            <FocusOn pos={focused} />
          </Map>
        </APIProvider>
      ) : (
        <div style={{ height: '100%', background: 'var(--map)', display: 'grid', placeItems: 'center' }}>
          <p className="muted">地図を出すには、Google マップのキー（VITE_GOOGLE_MAPS_KEY）を .env.local に入れてください</p>
        </div>
      )}
      <aside style={{ position: 'absolute', right: 16, top: 16, width: 360 }}><AlertsPanel /></aside>
      <aside style={{ position: 'absolute', left: 16, top: 16, width: 320, display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
          {[[working.length, '勤務中'], [deliveries.length, '配送'], [unassigned, '未割り当て']].map(([n, l], i) => (
            <div key={i} className="panel" style={{ padding: '10px 8px', textAlign: 'center' }}>
              <b className="num" style={{ display: 'block', fontSize: 22, color: i === 2 && n ? 'var(--alert)' : undefined }}>{n}</b><small className="muted">{l}</small>
            </div>
          ))}
        </div>
        <div className="panel" aria-label="ドライバー" style={{ padding: 12, display: 'grid', gap: 6, maxHeight: 'calc(100vh / var(--z) - 64px - 140px)', overflow: 'auto' }}>
          <b style={{ fontSize: 13 }}>ドライバー</b>
          {status.length ? status.map((r) => (
            <Link key={r.driverId} to={`/drivers/${r.driverId}`} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 8, borderRadius: 10, background: 'var(--surface-2)', color: 'inherit', textDecoration: 'none', outline: r.driverId === focus ? '2px solid #000' : 'none' }}>
              <span style={{ width: 12, height: 12, borderRadius: 4, background: r.state === 'no_signal' ? '#9e9e9e' : r.color }} />
              <span style={{ display: 'grid' }}>{names.driver(r.driverId)}<small className="muted">{r.shiftId ? `${r.distanceKm} km` : ''}{r.position ? `・位置 ${ago(r.position.at)}` : ''}</small></span>
              <span className="sp" style={{ flex: 1 }} /><span className={`pill ${STATE[r.state][1]}`}>{STATE[r.state][0]}</span>
            </Link>
          )) : <div className="empty">まだドライバーがいません</div>}
        </div>
      </aside>
    </div>
  );
}
